export type PlaybookItem = {
  heading: string
  body: string
  cta?: string
  /** First phrase in `cta` to render with a sun underline. */
  ctaUnderline?: string
  highlightHeading?: boolean
}

export type PracticeScheduleTemplate = {
  heading: string
  intro: string
  columns: readonly string[]
  paragraphs: readonly string[]
}

export type PlaybookAfterword = {
  heading: string
  paragraphs: readonly string[]
}

export type PlaybookSection = {
  id: string
  title: string
  intro?: string
  colorScheme?: 'royal' | 'grape' | 'blueberry'
  items: PlaybookItem[]
  pullQuote?: string
  cta?: string
  /** First phrase in `cta` to render semibold with a sun underline. */
  ctaUnderline?: string
  scheduleTemplate?: PracticeScheduleTemplate
  afterword?: PlaybookAfterword
  supportingParagraph?: string
}

export const PLAYBOOK_META = {
  title: 'The pre-season playbook for the whole household',
  metaTitle: 'Practice Schedule Template and Pre-Season Playbook',
  description:
    'Use our six-week pre-season playbook and a concise practice schedule template to choose a team, plan costs, finish forms, sort gear, and cover rides.',
  eyebrow: 'Free guide',
  subtitle:
    'Six weeks of useful decisions, with a practice schedule template when the dates arrive.',
  updatedAt: '2026-08-12',
} as const

export const PLAYBOOK_INTRO = [
  'The first practice is rarely where a sports season begins. It begins with the email that says registration closes Friday, practices are “likely” Tuesdays, and the uniform fee comes later. Six weeks gives the child and the adults enough room to choose the right season, see the real commitment, and work out week one before somebody is texting from the wrong field.',
  'Coaches sometimes use “practice plan” for the drills inside one session. Families need the outside view: which child, which date, what time, where, and who is handling each ride.',
  'That view matters once the dates arrive. It cannot answer whether travel soccer fits this fall, how the coach runs the team, what the season costs, or whether the sports physical is current. A practice schedule template fits inside a pre-season plan. It handles the dates while the playbook handles the decisions around them.',
  'Most of the raw information arrives by email. We connect to family Gmail and use context about your children, schools, and activities to surface the relevant event, deadline, form, or schedule change for review. A household account gives two parents separate logins and the same shared view of events and to-dos. You choose what goes on the calendar and who takes the wheel.',
]

export const PLAYBOOK_SECTIONS: PlaybookSection[] = [
  {
    id: 'six-weeks-out',
    title: 'Six weeks out: make the big call',
    intro:
      'Six weeks is early enough to ask the questions that can still change the answer.',
    items: [
      {
        heading: 'Does your child want this season?',
        body: 'Ask before registration, with the actual commitment in view. Playing with friends on Saturdays is a different proposition from weekday practices and travel weekends. Give them enough detail to answer the real question.',
      },
      {
        heading: 'Which level fits the household this time?',
        body: 'Compare the specific recreational, club, travel, or school option in front of you. Look at likely practice nights, game locations, tournaments, school breaks, and any overlap with siblings’ activities. The right answer can change from one season to the next.',
      },
      {
        heading: 'Who will coach this team?',
        body: 'A club name tells you little about the Tuesday experience. Ask who is assigned to this age group and talk with a family from the coach’s last season when you can.',
        highlightHeading: true,
      },
      {
        heading: 'What does the time commitment include?',
        body: 'Ask for arrival times, travel, tournaments, team meetings, and volunteer shifts alongside practices and games. “Two practices a week” leaves a lot of the week unspoken.',
      },
      {
        heading: 'Is the medical paperwork current?',
        body: 'Find the organization’s form and deadline before booking anything. The American Academy of Pediatrics says a sports physical should happen during routine care when possible. Otherwise, its [sports-physical guidance](https://www.aap.org/en/patient-care/preparticipation-physical-evaluation/) calls for the evaluation at least six weeks before the first preseason practice, leaving time for any follow-up.',
      },
    ],
    pullQuote:
      'If the dates are still provisional, treat them that way. A likely Tuesday practice is useful for deciding whether the season fits. It is a poor reason to reorganize every Tuesday through November.',
  },
  {
    id: 'four-weeks-out',
    title: 'Four weeks out: learn what the registration page leaves open',
    intro:
      'The base fee and the registration deadline are only part of the decision. Get answers for this program, team, and age group before you pay.',
    items: [
      {
        heading: 'What is the usual practice and game cadence?',
        body: 'Ask for days, start and arrival times, locations, and travel range. Ask how often those details tend to move.',
      },
      {
        heading: 'How many children are on the roster, and how is playing time handled?',
        body: 'Recreational and competitive programs may use different rules. The useful answer is the one for this team.',
      },
      {
        heading: 'What is the all-in cost?',
        body: 'Include registration, uniform, required gear, tournaments, travel, clinics, and team expenses. Ask about financial aid or payment plans if either affects the family’s decision.',
      },
      {
        heading: 'What is the refund policy?',
        body: 'Keep the written dates and conditions with the registration confirmation.',
      },
      {
        heading: 'What can be reused?',
        body: 'Get the current gear list, then try on last season’s uniform, cleats, skates, or protective equipment before buying replacements.',
      },
      {
        heading: 'What does the program ask of adults?',
        body: 'Snack duty, scorekeeping, team-parent work, field setup, and travel supervision all belong in the commitment.',
      },
      {
        heading: 'How are absences, conflicts, and changes handled?',
        body: 'Know which email, team app, or coach thread carries the official update and how the coach wants families to respond.',
      },
    ],
    supportingParagraph:
      'Families still choosing a program can use our [Bay Area sports guides](https://www.hellobabs.ai/youth-sports) to compare local options, season timing, and competitive levels.',
  },
  {
    id: 'two-weeks-out',
    title: 'Two weeks out: use the practice schedule template',
    intro:
      'Now the dates have earned a place on the household calendar. Add practices, games, tournaments, parent meetings, uniform pickup, and the weeks when school is closed but practice is still on.',
    scheduleTemplate: {
      heading: 'Practice schedule template',
      intro:
        'This compact practice schedule template catches the details that tend to disappear between the coach’s message and the car at 5:10.',
      columns: [
        'Child and team',
        'Date or recurring day',
        'Arrival, start, and end',
        'Location and entrance',
        'Ride there and ride home',
        'Latest source',
      ],
      paragraphs: [
        'Copy it into Google Sheets, Excel, Word, or a shared note if the calendar view is missing useful context. If the family calendar already shows all six fields, leave the table here. A template that creates a second maintenance job has missed the point.',
        'Use one row for a recurring practice and a separate row for an exception. Include the arrival time when it differs from the start time, the full field or entrance details, and both directions of the ride. Link or name the message that supplied the current schedule so the latest version is easy to find.',
      ],
    },
    items: [
      {
        heading: 'Keep events and actions separate.',
        body: 'Practice at 5:30 is an event. Upload the medical form by Friday is a to-do. One coach email can create both, and putting a deadline on the calendar at 5:30 does not make the form any more submitted.',
      },
      {
        heading: 'Confirm that registration is accepted rather than merely submitted.',
        body: 'Check the payment, medical form, consent forms, eligibility documents, and any uniform order that arrived through a different link.',
      },
    ],
  },
  {
    id: 'one-week-out',
    title: 'One week out: protect the first real week',
    intro:
      'The repeating schedule shows what usually happens. Week one has opinions of its own.',
    items: [
      {
        heading: 'Read the first seven days line by line.',
        body: 'Look for an earlier arrival, a different field, uniform handout, parent meeting, photo day, or school conflict.',
      },
      {
        heading: 'Try on the gear.',
        body: 'Check the uniform, shoes, protective equipment, practice layer, and water bottle while an exchange or loan is still possible.',
      },
      {
        heading: 'Confirm both directions of every ride.',
        body: 'The adult dropping off at 5:15 may be elsewhere at 6:45. Name the ride home and the backup before practice starts.',
      },
      {
        heading: 'Share the useful slice.',
        body: 'A partner, co-parent, nanny, grandparent, sitter, or carpool driver needs the date, place, handoff, and change channel for the commitment they are covering. They do not need a new workbook to maintain.',
      },
    ],
    supportingParagraph:
      'The child can own an age-appropriate part of the preparation, such as filling the water bottle or putting shin guards in the bag. An adult keeps responsibility for payment, medical forms, permissions, and transportation.',
    afterword: {
      heading: 'When a new email changes Tuesday',
      paragraphs: [
        'A calendar update and a handoff are two separate jobs. Change the event, then tell the person driving or covering pickup. The shared calendar can be perfectly current while the acting adult is still following the old plan.',
        'Keep the official message close to the event, especially when a field, arrival time, or date changes. When a later coach or league email reaches Gmail, we can flag the new detail so you can review it and correct the calendar. We do the read. The family keeps the judgment.',
      ],
    },
  },
  {
    id: 'end-of-season',
    title: 'At the end of the season, keep four notes',
    intro:
      'The last game ends, and next season starts forming in the parking lot. Most decisions can wait a week. Four notes are worth keeping while the details are fresh.',
    items: [
      {
        heading: 'The child’s read.',
        body: 'Ask what they liked, what they did not, and what they would want to change. Keep the answer beside the next registration decision.',
      },
      {
        heading: 'The coach conversation.',
        body: 'If the child wants to return, ask what level or focus would fit next season.',
      },
      {
        heading: 'The household read.',
        body: 'Note the night, cost, drive, or communication gap that should change the next choice.',
      },
      {
        heading: 'The thank-you.',
        body: 'Thank the coach, team parent, or carpool family who covered the rainy Thursday. A note in the child’s handwriting does the job.',
      },
    ],
  },
]

export const PLAYBOOK_CLOSING = {
  title: 'One last thing',
  body: 'Then let the schedule go quiet for a minute.',
  subtext:
    'The gear pile may have other plans. When the next coach email arrives, [let us do the first read](https://www.hellobabs.ai/faq#how-babs-works).',
}
